import React, { Component, createElement } from "react";
import { renderToString } from "react-dom/server";
import { Marker, InfoWindow, GoogleMap } from "react-google-maps";
import { GoogleMapsComponent } from "../GoogleMapsComponent";
import styles from "./style/Map.css";

import carIcon from "./images/ic-car.svg";
import destIcon from "./images/ic-pin-dest.svg?external";
import reachedIcon from "./images/ic-pin-reached.svg?external";
import { CarPopup } from "../CarPopup";
import { Vehicle } from "../../containers/Root";
import { DestinationPopup } from "../DestinationPopup";
import { SharedLinkPopup } from "./components/SharedLinkPopup/SharedLinkPopup";
import { isMobile } from "../../isMobile";

interface Props {
  vehicle: Vehicle;
  destination: {
    lat: number;
    lng: number;
    address: string;
  };
  arrived: boolean;
  fleetName: string;
  googleScriptHasLoaded: boolean;
  locationHasLoaded: boolean;
  lastUpdatedTime: number | null;
  isRealTimeUpdated: boolean;
}

interface State {
  carLabelVisible: boolean;
  destinationLabelVisible: boolean;
  clickedCopyButton: boolean;
}

export class Map extends Component<Props, State> {
  carIconWidth: number = 35;
  carIconHeight: number = 35;
  mapComponent?: GoogleMap;
  constructor(props: Props) {
    super(props);
    //@ts-ignore
    carIcon.defaultProps.xmlns = "http://www.w3.org/2000/svg";
    //@ts-ignore
    carIcon.defaultProps.version = "1.1";
    this.state = {
      carLabelVisible: false,
      destinationLabelVisible: false,
      clickedCopyButton: false
    };
  }
  static defaultProps = { vehicle: {} };

  onMapMounted = (mapComponent: GoogleMap) => {
    this.mapComponent = mapComponent;
    this.setBounds();
  };

  setBounds = () => {
    if (!this.props.googleScriptHasLoaded || !this.props.locationHasLoaded) {
      return;
    }
    const bounds = [
      {
        lat: this.props.arrived
          ? this.props.destination.lat
          : this.props.vehicle.location.lat,
        lng: this.props.arrived
          ? this.props.destination.lng
          : this.props.vehicle.location.lng
      },
      { lat: this.props.destination.lat, lng: this.props.destination.lng }
    ];
    if (this.mapComponent) {
      //@ts-ignore
      this.mapComponent.fitBounds(bounds);
    }
  };

  toggleCarLabel = () => {
    this.setState({
      carLabelVisible: !this.state.carLabelVisible,
      destinationLabelVisible: false
    });
  };

  toggleDestinationLabel = () => {
    this.setState({
      destinationLabelVisible: !this.state.destinationLabelVisible,
      carLabelVisible: false
    });
  };

  makeRotatedCarIconUrl = () => {
    let svgAsString = renderToString(createElement(carIcon));
    svgAsString = svgAsString.replace(
      "$heading",
      String(this.props.vehicle.heading)
    );
    svgAsString = svgAsString.replace("$x", (this.carIconWidth / 2).toString());
    svgAsString = svgAsString.replace(
      "$y",
      (this.carIconHeight / 2).toString()
    );
    return encodeURI("data:image/svg+xml;base64," + btoa(svgAsString));
  };

  sendEventToScrollSidebar = () => {
    const event = new CustomEvent("scroll-pagineta", {
      bubbles: true,
      cancelable: true
    });
    window.document.dispatchEvent(event);
  };

  renderArrivedMarkerPopup = () => {
    if (!this.state.carLabelVisible) return null;
    if (isMobile()) {
      this.sendEventToScrollSidebar();
      return null;
    }

    return (
      <InfoWindow onCloseClick={this.toggleCarLabel}>
        <CarPopup
          ignitionOn={this.props.vehicle.ignitionOn}
          speed={this.props.vehicle.speed}
          licensePlate={this.props.vehicle.licensePlate}
          brand={this.props.vehicle.brand}
          model={this.props.vehicle.model}
          address={this.props.vehicle.address}
          driverName={this.props.vehicle.driverName}
          lastUpdatedTime={this.props.lastUpdatedTime}
          isRealTimeUpdated={this.props.isRealTimeUpdated}
        />
      </InfoWindow>
    );
  };

  renderDestinationMarkerPopup = () => {
    if (!this.state.destinationLabelVisible) return null;

    if (isMobile()) {
      this.sendEventToScrollSidebar();

      return null;
    }

    return (
      <InfoWindow onCloseClick={this.toggleDestinationLabel}>
        <DestinationPopup destination={this.props.destination.address} />
      </InfoWindow>
    );
  };

  renderMap = (isLoaded: boolean) => {
    if (!isLoaded) return null;
    return (
      <GoogleMapsComponent
        fleetName={this.props.fleetName}
        loadingElementClassName={styles.map_loadingElement}
        mapElementClassName={styles.googleMapContainer}
        containerElementClassName={styles.container}
        center={this.props.vehicle.location}
        onMapMounted={this.onMapMounted}
        curvePathBegin={this.props.vehicle.location}
        curvePathEnd={this.props.destination}
        shouldDisplayCurve={!this.props.arrived}
      >
        <Marker
          position={{
            lat: this.props.vehicle.location.lat,
            lng: this.props.vehicle.location.lng
          }}
          onClick={this.toggleCarLabel}
          icon={{
            url: this.makeRotatedCarIconUrl(),
            anchor: new google.maps.Point(
              this.carIconWidth / 2,
              this.carIconHeight / 2
            )
          }}
        >
          {this.renderArrivedMarkerPopup()}
        </Marker>

        <Marker
          position={{
            lat: this.props.destination.lat,
            lng: this.props.destination.lng
          }}
          onClick={this.toggleDestinationLabel}
          icon={this.props.arrived ? reachedIcon : destIcon}
        >
          {this.renderDestinationMarkerPopup()}
        </Marker>
      </GoogleMapsComponent>
    );
  };

  render() {
    const isLoaded =
      this.props.googleScriptHasLoaded && this.props.locationHasLoaded;
    return (
      <div className={styles.map}>
        {isLoaded && <SharedLinkPopup fleetName={this.props.fleetName} />}
        {this.renderMap(isLoaded)}
      </div>
    );
  }
}
